import { toast } from 'react-hot-toast';
import { create } from 'zustand';
import api from '../config/axiosConfig';

const EMPTY_ATELIER = {
  id: '',
  slug: '',
  title: '',
  subtitle: '',
  description: '',
  icon: '🎓',
  accentColor: '#ec4899',
  tags: [],
  duration: 90,
  level: 'Accessible',
  videoCallEnabled: true,
  whiteboardEnabled: true,
  notesEnabled: true,
  theory: {
    title: '',
    slides: [],
  },
  exercises: [],
  resources: [],
  // mapping : quels templates de run débloquent cet atelier
  unlockedBy: [],
};

const useAtelierCreator = create((set, get) => ({

  // ── État ──────────────────────────────────────────────────────────────────
  formData: { ...EMPTY_ATELIER },
  ateliersList: [],   // liste pour le select "charger un atelier"
  mappings: {},       // TEMPLATE_TO_ATELIERS complet
  ui: {
    currentSection: 1,    // 1 | 2 | 3
    isNavOpen: true,
    isSaving: false,
    isDirty: false,
    isEditMode: false,    // false = création, true = édition
    editingId: null,
  },
  errors: {},

  // ── UI ────────────────────────────────────────────────────────────────────
  setSection: (n) => set(s => ({ ui: { ...s.ui, currentSection: n } })),
  toggleNav:  ()  => set(s => ({ ui: { ...s.ui, isNavOpen: !s.ui.isNavOpen } })),

  // ── Données ───────────────────────────────────────────────────────────────
  updateFormData: (path, value) => {
    set(s => {
      const keys   = path.split('.');
      const clone  = structuredClone(s.formData);
      let   cursor = clone;
      keys.slice(0, -1).forEach(k => {
        if (cursor[k] === undefined) cursor[k] = {};
        cursor = cursor[k];
      });
      cursor[keys[keys.length - 1]] = value;
      return { formData: clone, ui: { ...s.ui, isDirty: true } };
    });
  },

  // ── Slides ────────────────────────────────────────────────────────────────
  addSlide: () => set(s => ({
    formData: {
      ...s.formData,
      theory: {
        ...s.formData.theory,
        slides: [
          ...s.formData.theory.slides,
          { title: '', content: '' },
        ],
      },
    },
    ui: { ...s.ui, isDirty: true },
  })),

  updateSlide: (idx, field, value) => set(s => {
    const slides = [...s.formData.theory.slides];
    slides[idx] = { ...slides[idx], [field]: value };
    return {
      formData: { ...s.formData, theory: { ...s.formData.theory, slides } },
      ui: { ...s.ui, isDirty: true },
    };
  }),

  removeSlide: (idx) => set(s => ({
    formData: {
      ...s.formData,
      theory: {
        ...s.formData.theory,
        slides: s.formData.theory.slides.filter((_, i) => i !== idx),
      },
    },
    ui: { ...s.ui, isDirty: true },
  })),

  moveSlide: (idx, dir) => set(s => {
    const slides = [...s.formData.theory.slides];
    const swap   = idx + dir;
    if (swap < 0 || swap >= slides.length) return s;
    [slides[idx], slides[swap]] = [slides[swap], slides[idx]];
    return {
      formData: { ...s.formData, theory: { ...s.formData.theory, slides } },
      ui: { ...s.ui, isDirty: true },
    };
  }),

  // ── Exercices ─────────────────────────────────────────────────────────────
  addExercise: () => set(s => ({
    formData: {
      ...s.formData,
      exercises: [
        ...s.formData.exercises,
        {
          id:     `ex${Date.now().toString(36)}`,
          title:  '',
          type:   'writing',
          prompt: '',
        },
      ],
    },
    ui: { ...s.ui, isDirty: true },
  })),

  updateExercise: (idx, field, value) => set(s => {
    const exercises = [...s.formData.exercises];
    exercises[idx]  = { ...exercises[idx], [field]: value };
    return { formData: { ...s.formData, exercises }, ui: { ...s.ui, isDirty: true } };
  }),

  removeExercise: (idx) => set(s => ({
    formData: {
      ...s.formData,
      exercises: s.formData.exercises.filter((_, i) => i !== idx),
    },
    ui: { ...s.ui, isDirty: true },
  })),

  // ── Ressources ────────────────────────────────────────────────────────────
  addResource: () => set(s => ({
    formData: {
      ...s.formData,
      resources: [
        ...s.formData.resources,
        { title: '', url: '', type: 'link' },
      ],
    },
    ui: { ...s.ui, isDirty: true },
  })),

  updateResource: (idx, field, value) => set(s => {
    const resources = [...s.formData.resources];
    resources[idx]  = { ...resources[idx], [field]: value };
    return { formData: { ...s.formData, resources }, ui: { ...s.ui, isDirty: true } };
  }),

  removeResource: (idx) => set(s => ({
    formData: {
      ...s.formData,
      resources: s.formData.resources.filter((_, i) => i !== idx),
    },
    ui: { ...s.ui, isDirty: true },
  })),

  // ── Mappings ──────────────────────────────────────────────────────────────
  loadMappings: async () => {
    try {
      const res = await api.get('/admin/ateliers/mappings');
      set({ mappings: res.data });
    } catch (err) {
      console.error('Erreur chargement mappings:', err);
    }
  },

  saveMappings: async () => {
    try {
      await api.put('/admin/ateliers/mappings', get().mappings);
      toast.success('Mappings sauvegardés');
    } catch (err) {
      toast.error('Erreur sauvegarde mappings');
    }
  },

  updateMapping: (templateSlug, atelierIds) => set(s => ({
    mappings: { ...s.mappings, [templateSlug]: atelierIds },
  })),

  // ── CRUD ──────────────────────────────────────────────────────────────────
  loadAteliersList: async () => {
    try {
      const res = await api.get('/admin/ateliers');
      set({ ateliersList: res.data });
    } catch (err) {
      console.error('Erreur chargement ateliers:', err);
    }
  },

  loadAtelier: (atelier) => {
    set({
      formData: structuredClone(atelier),
      ui: {
        ...get().ui,
        isEditMode:  true,
        editingId:   atelier._id || atelier.id,
        isDirty:     false,
        currentSection: 1,
      },
    });
  },

  resetForm: () => set({
    formData: { ...EMPTY_ATELIER, slug: `atelier-${Date.now().toString(36)}` },
    ui: {
      ...get().ui,
      isEditMode:  false,
      editingId:   null,
      isDirty:     false,
      currentSection: 1,
    },
    errors: {},
  }),

  validate: () => {
    const { formData } = get();
    const errs = {};
    if (!formData.title?.trim())       errs.title    = 'Titre requis';
    if (!formData.slug?.trim())        errs.slug     = 'Slug requis';
    if (!formData.description?.trim()) errs.description = 'Description requise';
    if (!formData.theory?.title?.trim()) errs.theoryTitle = 'Titre théorie requis';
    set({ errors: errs });
    return Object.keys(errs).length === 0;
  },

  saveAtelier: async () => {
    const { formData, ui, validate } = get();
    if (!validate()) {
      toast.error('Champs manquants');
      return null;
    }
    set(s => ({ ui: { ...s.ui, isSaving: true } }));
    try {
      let res;
      if (ui.isEditMode && ui.editingId) {
        res = await api.put(`/admin/ateliers/${ui.editingId}`, formData);
      } else {
        res = await api.post('/admin/ateliers', formData);
      }
      const saved = res.data.atelier || res.data;
      set(s => ({
        ui: { ...s.ui, isSaving: false, isDirty: false, isEditMode: true, editingId: saved._id || saved.id },
        ateliersList: ui.isEditMode
          ? s.ateliersList.map(a => (a._id || a.id) === (saved._id || saved.id) ? saved : a)
          : [...s.ateliersList, saved],
      }));
      toast.success(ui.isEditMode ? 'Atelier mis à jour' : 'Atelier créé');
      return saved.slug;
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      toast.error(msg);
      set(s => ({ ui: { ...s.ui, isSaving: false } }));
      return null;
    }
  },

  deleteAtelier: async (atelierId) => {
    try {
      await api.delete(`/admin/ateliers/${atelierId}`);
      set(s => ({
        ateliersList: s.ateliersList.filter(a => (a._atelierId || a.atelierId) !== atelierId),
      }));
      toast.success('Atelier supprimé');
      get().resetForm();
    } catch (err) {
      toast.error('Erreur suppression');
    }
  },
}));

export default useAtelierCreator;